import React, { useContext, useEffect, useMemo, useRef } from 'react';

import { FormRuntimeContext } from '../../form-renderer/FormRuntimeContext';
import { CheckIcon } from '../../shared/Icons';
import { LoaderRoot } from './LoadingAnalysis.styles';
import { LoadingAnalysisConfig, resolveLoaderConfig } from './quizTypes';
import { usePipedText } from './usePipedText';
import { useRotatingIndex } from './useRotatingIndex';
import { useStagedProgress } from './useStagedProgress';

interface LoadingAnalysisProps {
  data: { blockConfig?: Partial<LoadingAnalysisConfig> };
  /** false on the builder canvas, true in the rendered form. */
  mutable?: boolean;
}

const TESTIMONIAL_INTERVAL_MS = 4000;

const LoadingAnalysis: React.FC<LoadingAnalysisProps> = ({ data, mutable }) => {
  const config = useMemo(() => resolveLoaderConfig(data?.blockConfig), [data?.blockConfig]);
  const { advanceStep } = useContext(FormRuntimeContext);
  const piped = usePipedText().html;
  const isLive = mutable !== false;

  const { percents, done } = useStagedProgress(
    config.stages.length,
    config.secondsPerStage,
    isLive,
  );
  const quoteIndex = useRotatingIndex(
    config.testimonials.length,
    TESTIMONIAL_INTERVAL_MS,
    isLive,
  );

  const advancedRef = useRef(false);
  useEffect(() => {
    if (!done || !config.autoAdvance || !advanceStep || advancedRef.current) return;
    advancedRef.current = true;
    advanceStep();
  }, [done, config.autoAdvance, advanceStep]);

  const quote = config.testimonials[quoteIndex];

  return (
    <LoaderRoot className="quiz-block">
      {config.title && (
        <h2 className="quiz-title" dangerouslySetInnerHTML={{ __html: piped(config.title) }} />
      )}

      <div className="loader-stages">
        {config.stages.map((stage, index) => {
          const percent = percents[index] ?? 0;
          return (
            <div
              key={stage.id}
              className={`loader-stage${percent === 0 ? ' is-pending' : ''}`}
            >
              <div className="loader-stage-head">
                <span>{stage.label}</span>
                <span className="loader-stage-value">
                  {percent >= 100 ? (
                    <span className="loader-check">
                      <CheckIcon size={12} />
                    </span>
                  ) : (
                    `${percent}%`
                  )}
                </span>
              </div>
              <div
                className="loader-track"
                role="progressbar"
                aria-valuenow={percent}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={stage.label}
              >
                <div className="loader-fill" style={{ width: `${percent}%` }} />
              </div>
            </div>
          );
        })}
      </div>

      {config.subtitle && (
        <p className="quiz-subtitle" dangerouslySetInnerHTML={{ __html: piped(config.subtitle) }} />
      )}

      {quote && (
        <div className="quiz-card loader-testimonial">
          <div className="loader-quote" key={quote.id}>
            <div className="loader-stars" aria-label="5 out of 5 stars">
              ★★★★★
            </div>
            <p className="loader-quote-text">“{quote.text}”</p>
            <div className="loader-author">
              {quote.imageUrl?.trim() && (
                <img className="loader-avatar" src={quote.imageUrl.trim()} alt="" />
              )}
              <span>{quote.name}</span>
            </div>
          </div>
          {config.testimonials.length > 1 && (
            <div className="loader-dots" aria-hidden="true">
              {config.testimonials.map((item, index) => (
                <span
                  key={item.id}
                  className={`loader-dot${index === quoteIndex ? ' is-active' : ''}`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </LoaderRoot>
  );
};

export default LoadingAnalysis;
